import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import {
  Send,
  MailOpen,
  MessageSquareReply,
  Undo2,
  Plus,
} from 'lucide-react';
import type { Campaign } from '@revbase/shared';
import { db } from '../lib/firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import {
  Stat,
  StatGrid,
  Button,
  Badge,
  CardTitle,
  EmptyState,
  Skeleton,
} from '../components/ui.js';

interface CampaignRow extends Campaign {
  id: string;
}

/** Map a campaign status to a semantic badge variant. */
function statusVariant(status: string): 'good' | 'warn' | 'info' | 'neutral' {
  switch (status) {
    case 'active':
    case 'running':
      return 'good';
    case 'paused':
      return 'warn';
    case 'draft':
      return 'neutral';
    default:
      return 'info';
  }
}

export function Dashboard() {
  const { tenantId } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    const q = query(
      collection(db, `tenants/${tenantId}/campaigns`),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, (snap) => {
      setCampaigns(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Campaign) })),
      );
      setLoading(false);
    });
  }, [tenantId]);

  const totals = campaigns.reduce(
    (acc, c) => ({
      sent: acc.sent + c.stats.sent,
      opened: acc.opened + c.stats.opened,
      replied: acc.replied + c.stats.replied,
      bounced: acc.bounced + c.stats.bounced,
    }),
    { sent: 0, opened: 0, replied: 0, bounced: 0 },
  );

  const pct = (n: number, d: number) =>
    d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '0%';

  return (
    <div className="space-y-6">
      {/* Hero band: the page's focal moment. A bold emerald surface with the
          north-star reply rate large, plus the primary CTA. */}
      <section className="hero-band px-6 py-8 sm:px-10 sm:py-10">
        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-100">
              Overview
            </p>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Dashboard
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-emerald-50/80">
              Everything your mailboxes have sent, opened, and replied to, in one
              place.
            </p>
            <div className="mt-6">
              <Link
                to="/campaigns/new"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-800 shadow-soft-md transition duration-200 hover:bg-brand-50 active:translate-y-px"
              >
                <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
                New campaign
              </Link>
            </div>
          </div>
          <div className="shrink-0 lg:text-right">
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-100">
              Reply rate
            </p>
            <p className="mt-1 font-display text-5xl font-bold leading-none tabular-nums text-white sm:text-6xl">
              {pct(totals.replied, totals.sent)}
            </p>
            <p className="mt-2 text-sm text-emerald-50/80">
              {totals.replied} {totals.replied === 1 ? 'reply' : 'replies'} from{' '}
              {totals.sent} sent
            </p>
          </div>
        </div>
      </section>

      <StatGrid>
        <Stat label="Sent" value={totals.sent} icon={Send} />
        <Stat
          label="Opened"
          value={totals.opened}
          icon={MailOpen}
          trend={`${pct(totals.opened, totals.sent)} open rate`}
        />
        <Stat
          label="Replied"
          value={totals.replied}
          icon={MessageSquareReply}
          trend={`${pct(totals.replied, totals.sent)} reply rate`}
        />
        <Stat
          label="Bounced"
          value={totals.bounced}
          icon={Undo2}
          trend={`${pct(totals.bounced, totals.sent)} bounce rate`}
        />
      </StatGrid>

      <section>
        <CardTitle icon={Send} className="mb-4 text-lg">
          Campaigns
        </CardTitle>
        {loading ? (
          <div className="card divide-y divide-slate-100 overflow-hidden p-0">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between gap-4 px-6 py-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-4 w-40" />
              </div>
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <EmptyState
            icon={Send}
            title="No campaigns yet"
            description="Create your first sequence to start reaching prospects from your own mailbox."
            action={
              <Link to="/campaigns/new">
                <Button icon={Plus}>New campaign</Button>
              </Link>
            }
          />
        ) : (
          <div className="card overflow-x-auto p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th className="num">Sent</th>
                  <th className="num">Replied</th>
                  <th className="num">Bounced</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/campaigns/${c.id}/edit`)}
                    className="cursor-pointer"
                  >
                    <td>
                      <Link
                        to={`/campaigns/${c.id}/edit`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-slate-900 transition-colors hover:text-brand-700"
                      >
                        {c.name}
                      </Link>
                      <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                        <Badge variant={statusVariant(c.status)} dot>
                          {c.status}
                        </Badge>
                        <span>
                          {c.mailboxIds.length} mailbox
                          {c.mailboxIds.length === 1 ? '' : 'es'}
                        </span>
                      </div>
                    </td>
                    <td className="num text-slate-900">{c.stats.sent}</td>
                    <td className="num font-semibold text-brand-700">
                      {c.stats.replied}
                    </td>
                    <td
                      className={clsx(
                        'num',
                        c.stats.bounced > 0 ? 'text-red-600' : 'text-slate-400',
                      )}
                    >
                      {c.stats.bounced}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
